import { collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { updateEmail } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { GetPersonalInformationReturnType, User } from './types';

export const getPersonalInformation = async (
  id: string
): Promise<GetPersonalInformationReturnType> => {
  try {
    const docRef = doc(db, 'users', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return {
        errorMessage:
          'Det finns ingen användare med det inloggade personnumret...'
      };
    }
    const data = docSnap.data() as User;
    return { data };
  } catch (error) {
    return {
      errorMessage: 'Kunde inte hämta personlig information...'
    };
  }
};

export const savePersonalInformation = async (data: {
  id: string;
  email: string;
  phoneNumber: string;
  firstName?: string;
  lastName?: string;
}): Promise<{ errorMessage?: string } | undefined> => {
  try {
    // Uppdatera Firestore först
    const docRef = doc(db, 'users', data.id);
    const updateData: any = {
      email: data.email,
      phoneNumber: data.phoneNumber,
      emailVerified: true // Markera att användaren har angett sin riktiga email
    };
    
    // Lägg till firstName och lastName om de finns
    if (data.firstName) updateData.firstName = data.firstName;
    if (data.lastName) updateData.lastName = data.lastName;
    
    await updateDoc(docRef, updateData);

    // Synka email/phone till föreningens medlemsförteckning (housing.members)
    // för alla housing där ownedBy === user.uid. Användarens egna inmatning
    // skriver alltid över befintligt värde i members-arrayen.
    try {
      await syncContactToHousingMembers(data.id);
    } catch (syncError) {
      // Logga men låt inte sync-fel blockera profil-spar
      // eslint-disable-next-line no-console
      console.warn('Kunde inte synka kontaktinfo till housing.members:', syncError);
    }

    // Uppdatera Firebase Auth-användarens email om det är en BankID-användare med temp email
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.email?.includes('@bankid.local')) {
      try {
        await updateEmail(currentUser, data.email);
        // // console.log('Firebase Auth email uppdaterad från', currentUser.email, 'till', data.email);
      } catch (emailUpdateError) {
        // Vi fortsätter ändå eftersom Firestore-uppdateringen lyckades
      }
    }

    return;
  } catch (error) {
    return {
      errorMessage: 'Kunde inte spara personlig information...'
    };
  }
};

/**
 * Sync user's email + phone into housing.members[] for all housing docs
 * where ownedBy === uid. The user's own input always wins over any
 * previously imported value. If the user's name doesn't match any existing
 * member-row in the housing doc, a new member row is appended so that the
 * member registry always shows the owner's contact info.
 */
async function syncContactToHousingMembers(uid: string): Promise<void> {
  const userSnap = await getDoc(doc(db, 'users', uid));
  if (!userSnap.exists()) return;
  const userData = userSnap.data() as User;

  const email = userData.email || '';
  const phone = userData.phoneNumber || '';
  const userFirst = (userData.firstName || '').toLowerCase().trim();
  const userLast = (userData.lastName || '').toLowerCase().trim();

  if (!email && !phone) return;

  const housingSnap = await getDocs(query(
    collection(db, 'housing'),
    where('ownedBy', '==', uid)
  ));

  if (housingSnap.empty) return;

  const nowIso = new Date().toISOString();

  await Promise.all(housingSnap.docs.map(async (hDoc) => {
    const h = hDoc.data() as any;
    const members = Array.isArray(h.members) ? [...h.members] : [];

    let matched = false;
    const newMembers = members.map((m: any) => {
      const mFirst = (m?.firstName || '').toLowerCase().trim();
      const mLast = (m?.lastName || '').toLowerCase().trim();
      if (userFirst && userLast && mFirst === userFirst && mLast === userLast) {
        matched = true;
        return { ...m, email, phone };
      }
      return m;
    });

    // Saknas medlemmen i listan? Lägg till så Hyria-admin alltid ser ägaren.
    if (!matched && userFirst && userLast) {
      newMembers.push({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        legalEntity: '',
        email,
        phone,
      });
    }

    await updateDoc(hDoc.ref, {
      members: newMembers,
      hasMemberInfo: newMembers.length > 0,
      updatedAt: nowIso,
    });
  }));
}
